import type { Board } from "../board";
import type { Direction } from "../direction";
import { directionDelta } from "../direction";
import { MovementEffect } from "../effects/movementEffect";
import { Event } from "../events/event";
import { EventType } from "../events/eventType";
import type { Player } from "../player";
import { Landmine } from "../things/landmine";
import { Plant } from "../things/plant";
import { Sound } from "../things/sounds/sound";
import { SoundId } from "../things/sounds/soundId";
import { WaterTile } from "../things/waterTile";
import { Weapon } from "../weapon";
import { WeaponVisual } from "../weaponVisual";

export class LandmineWeapon extends Weapon {
	static key = "LandmineWeapon";
	constructor(owner: Player, ammo: number = 5) {
		super(owner, ammo);
	}

	override canAutoFire(): boolean {
		return false;
	}

	override getWeaponVisual(): WeaponVisual {
		return WeaponVisual.DEPLOYER;
	}

	fire(direction: Direction, board: Board): void {
		const targetXY = this.owner.xy.add(directionDelta(direction));

		// Validate placement
		if (!board.isInBounds(targetXY) || !board.isPassable(targetXY) || board.hasMob(targetXY)) {
			this.ammo++; // Re-add ammo since pullTrigger already consumed it
			board.addEvent(new Event(EventType.CANT_PUT_MINE_HERE));
			board.turnAbortedByPlayer = true;
			return;
		}

		const thingsAtTarget = board.getThingsAt(targetXY);
		const hasBlocker = thingsAtTarget.some(
			(t) => t instanceof Landmine || t instanceof Plant || t instanceof WaterTile,
		);
		if (hasBlocker) {
			this.ammo++;
			board.addEvent(new Event(EventType.CANT_PUT_MINE_HERE));
			board.turnAbortedByPlayer = true;
			return;
		}

		board.addNonPhysicalThing(new Sound(SoundId.LANDMINE_DEPLOY));
		const landmine = new Landmine(targetXY);
		board.addThing(landmine);
		board.addEffect(new MovementEffect(landmine, this.owner.xy, targetXY));
	}

	static deserialize(data: any, owner: Player): LandmineWeapon {
		const gun = new LandmineWeapon(owner);
		gun.ammo = data.ammo;
		return gun;
	}
}
